import {
  getMultipleChoiceDestinations,
  isMultipleChoiceStep,
  parseMultipleChoiceConfig,
  unlinkMultipleChoiceDestinationFromConfig,
} from './multiple-choice-step.domain.js';

export type SopStepOutgoingEdgeKind = 'next' | 'yes' | 'no' | 'option';

export type SopStepOutgoingEdge = {
  kind: SopStepOutgoingEdgeKind;
  optionId?: string;
  label: string;
  targetId: number | null;
};

export type SopStepEdgeSource = {
  archetype?: string | null;
  choice_positive_label?: string | null;
  choice_negative_label?: string | null;
  next_step?: unknown;
  yes_next_step?: unknown;
  no_next_step?: unknown;
  config?: unknown;
};

function normalizeStepId(value: unknown): number | null {
  if (value == null || value === '') return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  return numeric;
}

export function getSopStepOutgoingEdges(step: SopStepEdgeSource): SopStepOutgoingEdge[] {
  const archetype = String(step.archetype || '').toUpperCase();
  if (archetype === 'TERMINAL') return [];

  if (archetype === 'BINARY_CHOICE') {
    return [
      {
        kind: 'yes',
        label: String(step.choice_positive_label || 'Yes').trim() || 'Yes',
        targetId: normalizeStepId(step.yes_next_step),
      },
      {
        kind: 'no',
        label: String(step.choice_negative_label || 'No').trim() || 'No',
        targetId: normalizeStepId(step.no_next_step),
      },
    ];
  }

  if (isMultipleChoiceStep(archetype)) {
    const options = parseMultipleChoiceConfig(step.config).options;
    return options.map((option) => ({
      kind: 'option' as const,
      optionId: option.id,
      label: option.label.trim() || 'Choice',
      targetId: normalizeStepId(option.next_step_id),
    }));
  }

  return [
    {
      kind: 'next',
      label: 'Next',
      targetId: normalizeStepId(step.next_step),
    },
  ];
}

export function getSopStepIncomingEdgeTargetIds(step: SopStepEdgeSource): number[] {
  if (!isMultipleChoiceStep(step.archetype)) return [];
  return getMultipleChoiceDestinations(step.config)
    .map((entry) => entry.nextStepId)
    .filter((id): id is number => id != null);
}

export type SopStepUnlinkPatch = {
  next_step?: null;
  yes_next_step?: null;
  no_next_step?: null;
  config?: unknown;
};

function stepIdMatchesDeleted(value: unknown, deletedStepId: number): boolean {
  if (value == null || value === '') return false;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 && numeric === deletedStepId;
}

/** Build update patches that clear inbound references to a deleted step ID. */
export function buildUnlinkPatchesForDeletedStep(
  step: SopStepEdgeSource,
  deletedStepId: number,
): SopStepUnlinkPatch | null {
  const patch: SopStepUnlinkPatch = {};

  if (stepIdMatchesDeleted(step.next_step, deletedStepId)) {
    patch.next_step = null;
  }
  if (stepIdMatchesDeleted(step.yes_next_step, deletedStepId)) {
    patch.yes_next_step = null;
  }
  if (stepIdMatchesDeleted(step.no_next_step, deletedStepId)) {
    patch.no_next_step = null;
  }

  if (isMultipleChoiceStep(step.archetype)) {
    const configPatch = unlinkMultipleChoiceDestinationFromConfig(step.config, deletedStepId);
    if (configPatch) {
      patch.config = configPatch;
    }
  }

  return Object.keys(patch).length > 0 ? patch : null;
}
